/**
 * BrowserCompatibilityBanner
 *
 *  Two preventive UX layers for visitors arriving via Facebook,
 *  Messenger or Instagram in-app browsers (IABs), or any environment
 *  that has localStorage / cookies blocked.
 *
 *  Layer 1 (P0 Fix #1) — IAB DETECTION BANNER
 *    Small, dismissible notice rendered above any other UI on the most
 *    bounce-prone routes (landing, signup, job funnel, login, etc).
 *    Tells the user "You're inside Facebook's browser — tap menu to
 *    open in your real browser for a smoother experience."
 *
 *  Layer 2 (P0 Fix #2) — PRE-FLIGHT STORAGE CHECK
 *    On first paint, tests whether `localStorage` writes and `document.
 *    cookie` writes both survive. If either is blocked, surfaces a
 *    clear plain-English notice telling the user how to fix it.
 *
 *  Analytics events fired (see /api/veritrade/track whitelist):
 *    - iab_detected (vendor, route)
 *    - iab_warning_dismissed
 *    - iab_open_in_browser_clicked
 *    - storage_blocked (which: localStorage|cookies|both)
 *    - cookies_blocked
 *
 *  DO NOT change auth logic, signup flow, job flow, messages,
 *  permissions — this file is UX + diagnostics only.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Info, X, ExternalLink, ShieldAlert } from "lucide-react";
import {
  isIOS, isFacebookBrowser, isInstagramBrowser, isSocialInAppBrowser,
  getParentAppName,
} from "@/utils/browserDetect";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

// Routes where the banner should show. Matched as either exact path or prefix.
const SHOW_ON_ROUTES = [
  { type: "exact", path: "/" },
  { type: "prefix", path: "/app" },
  { type: "prefix", path: "/login" },
  { type: "prefix", path: "/signup" },
  { type: "prefix", path: "/post-job" },
  { type: "prefix", path: "/join-tradesperson" },
  { type: "prefix", path: "/join-customer" },
  { type: "prefix", path: "/become-affiliate" },
  { type: "prefix", path: "/vericlean" },
  { type: "prefix", path: "/verisite" },
  { type: "prefix", path: "/veritrain" },
  { type: "prefix", path: "/affiliate/login" },
  { type: "prefix", path: "/affiliate/signup" },
  { type: "prefix", path: "/affiliate/canvasser/signup" },
];

const matchesRoute = (pathname) => {
  return SHOW_ON_ROUTES.some((r) =>
    r.type === "exact" ? pathname === r.path : pathname.startsWith(r.path)
  );
};

const DISMISS_KEY = "vt_iab_banner_dismissed_v1";
const STORAGE_PROBE_KEY = "__vt_storage_probe__";

/** Detect the in-app browser flavour. Returns null if not in an IAB.
 *  Uses the shared utility so the detection rules stay in one place.
 */
const detectIab = () => {
  if (!isSocialInAppBrowser()) {
    // Messenger UA doesn't always include FBAN/FBAV — detect explicitly.
    if (typeof navigator === "undefined") return null;
    if (/Messenger|MessengerForiOS|MessengerLite/i.test(navigator.userAgent || "")) return "messenger";
    return null;
  }
  // FB in-app browser typically also identifies Messenger via the same UA.
  if (typeof navigator !== "undefined" && /Messenger|MessengerForiOS|MessengerLite/i.test(navigator.userAgent || "")) return "messenger";
  if (isFacebookBrowser()) return "facebook";
  if (isInstagramBrowser()) return "instagram";
  return "social";
};

/** Probe storage. Returns {localStorage:boolean, cookies:boolean}. */
const probeStorage = () => {
  let localStorageOk = false;
  let cookiesOk = false;
  try {
    window.localStorage.setItem(STORAGE_PROBE_KEY, "1");
    if (window.localStorage.getItem(STORAGE_PROBE_KEY) === "1") localStorageOk = true;
    window.localStorage.removeItem(STORAGE_PROBE_KEY);
  } catch { localStorageOk = false; }
  try {
    document.cookie = `${STORAGE_PROBE_KEY}=1; path=/; SameSite=Lax`;
    cookiesOk = document.cookie.indexOf(STORAGE_PROBE_KEY) !== -1;
    // Best-effort cleanup
    document.cookie = `${STORAGE_PROBE_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  } catch { cookiesOk = false; }
  return { localStorage: localStorageOk, cookies: cookiesOk };
};

/** Lightweight, fire-and-forget analytics ping (matches existing /api/veritrade/track). */
const track = (event_type, metadata = {}) => {
  try {
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const body = JSON.stringify({ event_type, path, metadata });
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(`${API}/api/veritrade/track`, blob);
    } else {
      fetch(`${API}/api/veritrade/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch { /* swallow */ }
};

const safeReadDismissed = () => {
  try { return window.localStorage.getItem(DISMISS_KEY) === "1"; }
  catch { return false; }
};
const safeWriteDismissed = () => {
  try { window.localStorage.setItem(DISMISS_KEY, "1"); }
  catch { /* ignore — storage blocked, banner will redisplay (acceptable) */ }
};

export default function BrowserCompatibilityBanner() {
  const [pathname, setPathname] = useState(() =>
    typeof window !== "undefined" ? window.location.pathname : "/"
  );
  const [iab] = useState(() => detectIab());
  const [storage] = useState(() => probeStorage());
  const [dismissed, setDismissed] = useState(() => safeReadDismissed());
  const trackedOnce = useRef(false);

  // Listen for SPA path changes (react-router history) so the banner
  // appears/disappears correctly without a full reload.
  useEffect(() => {
    const onChange = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onChange);
    // Patch pushState / replaceState so we get notified of programmatic nav
    const wrap = (fn) => function (...args) {
      const ret = fn.apply(this, args);
      window.dispatchEvent(new Event("popstate"));
      return ret;
    };
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = wrap(origPush);
    window.history.replaceState = wrap(origReplace);
    return () => {
      window.removeEventListener("popstate", onChange);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  // Fire iab_detected + storage_blocked diagnostics events once per session.
  useEffect(() => {
    if (trackedOnce.current) return;
    trackedOnce.current = true;
    if (iab) track("iab_detected", { vendor: iab, route: pathname });
    if (!storage.localStorage && !storage.cookies) track("storage_blocked", { which: "both" });
    else if (!storage.localStorage) track("storage_blocked", { which: "localStorage" });
    else if (!storage.cookies) track("cookies_blocked");
  }, [iab, storage, pathname]);

  const showOnThisRoute = useMemo(() => matchesRoute(pathname), [pathname]);

  // Storage-blocked notice takes priority — it actually breaks sign in / submit.
  // It is NOT dismissible because the action wouldn't work anyway.
  const showStorageError = (!storage.localStorage || !storage.cookies);

  // Only the IAB banner is dismissible.
  const showIabBanner = !!iab && !dismissed && !showStorageError;

  if (!showOnThisRoute) return null;
  if (!showIabBanner && !showStorageError) return null;

  const onDismiss = () => {
    setDismissed(true);
    safeWriteDismissed();
    track("iab_warning_dismissed", { vendor: iab });
  };
  const onOpenInBrowserClick = () => {
    track("iab_open_in_browser_clicked", { vendor: iab });
    // We can't programmatically launch the OS browser, but we can copy
    // the URL so the user can paste it.
    try {
      navigator.clipboard?.writeText?.(window.location.href);
    } catch { /* ignore */ }
  };

  const vendorLabel = iab === "facebook" ? "Facebook" :
                      iab === "messenger" ? "Messenger" :
                      iab === "instagram" ? "Instagram" :
                      (getParentAppName() || "this app");

  const iosTip = "Tap " + "\u22EF" + " (menu) at the bottom → Open in Safari";
  const androidTip = "Tap " + "\u22EE" + " (menu) at the top-right → Open in Chrome";

  return (
    <div
      className="w-full px-3 pt-3 sm:px-4 sm:pt-4"
      data-testid="browser-compat-banner-root"
      role="region"
      aria-label="Browser compatibility notice"
    >
      {showStorageError && (
        <div
          className="mx-auto max-w-3xl rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 shadow-sm flex items-start gap-3"
          data-testid="storage-blocked-banner"
        >
          <div className="mt-0.5 shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-rose-100 text-rose-600">
            <ShieldAlert className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-rose-800 text-sm leading-tight">
              Your browser is blocking storage we need
            </p>
            <p className="text-[13px] text-rose-700 mt-1 leading-relaxed">
              We can&apos;t sign you in or save your job here. Please open VeriTrade in your device&apos;s
              regular browser (Safari or Chrome) and try again.
            </p>
            <p className="text-[12px] text-rose-700/80 mt-2">
              {isIOS() ? iosTip : androidTip}
            </p>
          </div>
        </div>
      )}

      {showIabBanner && (
        <div
          className="mx-auto max-w-3xl rounded-xl border border-orange-200 bg-orange-50/95 backdrop-blur-sm px-4 py-3 shadow-sm flex items-start gap-3"
          data-testid="iab-banner"
          data-iab-vendor={iab}
        >
          <div className="mt-0.5 shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-600">
            <Info className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-900 text-sm leading-tight">
              You&apos;re viewing VeriTrade inside the {vendorLabel} browser
            </p>
            <p className="text-[13px] text-slate-700 mt-1 leading-relaxed">
              For the best experience, tap the menu and choose{" "}
              <span className="font-semibold">Open in Browser</span>.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onOpenInBrowserClick}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-orange-700 hover:text-orange-800 underline underline-offset-2"
                data-testid="iab-open-in-browser-btn"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Copy link to open in your browser
              </button>
              <span className="text-[12px] text-slate-500">
                ({isIOS() ? iosTip : androidTip})
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss notice"
            data-testid="iab-banner-dismiss"
            className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-slate-500 hover:text-slate-700 hover:bg-orange-100 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
